"use client";

import React, { useCallback, useEffect, useState } from "react";
import { Badge } from "../../../../ui/components/Badge";
import { Button } from "../../../../ui/components/Button";
import { IconButton } from "../../../../ui/components/IconButton";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import { TextArea } from "../../../../ui/components/TextArea";
import { TextField } from "../../../../ui/components/TextField";
import { ToggleGroup } from "../../../../ui/components/ToggleGroup";
import { DialogLayout } from "../../../../ui/layouts/DialogLayout";
import { FeatherArrowUpRight } from "@subframe/core";
import { FeatherFileText } from "@subframe/core";
import { FeatherPlus } from "@subframe/core";
import { FeatherUser } from "@subframe/core";
import { FeatherX } from "@subframe/core";
import { usersAPI } from "../../../../services/api";
import { getUser } from "../../../data/storage/authStorage";
import { SearchableDropdown } from "../../../presentation/components/SearchableDropdown";

// Clean Architecture: Use hooks
import { useCreateQuery } from "../hooks/useCreateQuery";
import { useCreateSubQuery } from "../hooks/useCreateSubQuery";
import { QueryPriority, QuerySource } from "../../domain/entities/QueryEntity";

type SourceDocType =
  | "invoice"
  | "bill"
  | "bank"
  | "journal"
  | "credit_note"
  | string;

interface SourceDoc {
  type: SourceDocType;
  id: number;
  filename?: string;
  amount?: string;
  viewUrl?: string;
}

interface QueryInitialValues {
  title?: string;
  description?: string;
  priority?: "Low" | "Medium" | "High";
  assignTo?: string;
}

interface QueryRaiseModalProps {
  open: boolean;
  parentQueryId?: number;
  isCreateSubQuery?: boolean;
  onOpenChange: (open: boolean) => void;
  sourceDoc?: SourceDoc;
  initialValues?: QueryInitialValues;
  complianceType?: string; // Compliance type for compliance source queries
  onSubmit?: (payload: {
    title: string;
    description: string;
    priority: "Low" | "Medium" | "High";
    assignTo: string;
    sourceDoc?: SourceDoc;
  }) => Promise<void>;
  onQueryRaised?: (success: boolean) => void;
}

const QueryRaiseModal: React.FC<QueryRaiseModalProps> = ({
  open,
  isCreateSubQuery = false,
  parentQueryId,
  onOpenChange,
  initialValues,
  sourceDoc,
  complianceType,
  onSubmit,
  onQueryRaised,
}) => {
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState<"Low" | "Medium" | "High">("Medium");
  const [assignTo, setAssignTo] = useState<number | null>(null);
  const [assignToError, setAssignToError] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{
    id: number;
    email: string;
    userName: string;
    role: string;
  } | null>(null);

  // Check if this is a compliance query
  const isComplianceQuery =
    sourceDoc?.type === "compliance" || !!complianceType;

  // Clean Architecture: Use hooks instead of direct API calls
  const { createQuery, creating: isCreatingQuery } = useCreateQuery();
  const { createSubQuery, creating: isCreatingSubQuery } = useCreateSubQuery();

  const submitting = isCreatingQuery || isCreatingSubQuery;

  // Reset form when modal opens
  useEffect(() => {
    if (!open) return;
    setTitle(initialValues?.title ?? "");
    setDescription(initialValues?.description ?? "");
    setPriority(initialValues?.priority ?? "Medium");
    const initialAssignTo = initialValues?.assignTo
      ? Number(initialValues.assignTo)
      : null;
    setAssignTo(initialAssignTo);
    setSelectedUser(null);
    setAssignToError(false);
  }, [open, initialValues]);

  // Helper function to convert snake_case to Title Case
  const formatRole = (role: string): string => {
    if (!role) return "";
    return role
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  // Helper function to format compliance type (e.g., "GST_MONTHLY" -> "GST MONTHLY")
  const formatComplianceType = (complianceType: string): string => {
    if (!complianceType) return "";
    return complianceType
      .split("_")
      .map((word) => word.toUpperCase())
      .join(" ");
  };

  // Fetch users function for SearchableDropdown
  const fetchUsers = useCallback(
    async (searchTerm: string, limit: number, offset: number) => {
      try {
        const result = await usersAPI.getUsersPaginated(
          searchTerm,
          limit,
          offset
        );
        const currentUser = getUser();

        // Filter out the current user and map the data
        let filteredUsers = (result.items || [])
          .filter((u: any) => {
            return currentUser ? String(u.id) !== String(currentUser.id) : true;
          })
          .map((u: any) => ({
            id: Number(u.id),
            email: String(u.email || ""),
            userName: String(u.userName || ""),
            role: String(u.role || ""),
          }));

        // If this is a compliance query, filter to only show client_view users
        if (isComplianceQuery) {
          filteredUsers = filteredUsers.filter(
            (u: {
              id: number;
              email: string;
              userName: string;
              role: string;
            }) => u.role.toLowerCase() === "client_view"
          );
        }

        return {
          items: filteredUsers,
          hasMore: result.hasMore || false,
        };
      } catch (error) {
        console.error("Failed to fetch users:", error);
        return {
          items: [],
          hasMore: false,
        };
      }
    },
    [isComplianceQuery]
  );

  const handleSubmit = async () => {
    // Validation
    if (!title || title.trim().length < 3) return;
    if (!assignTo || assignTo === null) {
      setAssignToError(true);
      return;
    }
    // For compliance queries, compliance type is required
    if (isComplianceQuery && !complianceType) {
      return; // Should not happen, but safety check
    }

    try {
      // Convert priority to lowercase for API
      const priorityApi = priority.toLowerCase() as QueryPriority;

      // Determine source and sourceId
      let source: QuerySource = QuerySource.GENERAL;
      let sourceId: number | null = null;

      if (sourceDoc) {
        const t = String(sourceDoc.type || "").toLowerCase();
        if (t === "bank") source = QuerySource.TRANSACTION;
        else if (t === "invoice") source = QuerySource.INVOICE;
        else if (t === "bill") source = QuerySource.BILL;
        else if (t === "journal") source = QuerySource.JOURNAL;
        else if (t === "credit_note") source = QuerySource.CREDIT_NOTE;
        else if (t === "compliance") source = QuerySource.COMPLIANCE;

        const parsed = Number(sourceDoc.id);
        sourceId = Number.isFinite(parsed) ? parsed : null;
      }

      const assignedToId = assignTo;

      // If creating a sub-query, use the sub-query hook
      if (isCreateSubQuery && parentQueryId) {
        const result = await createSubQuery({
          parentQueryId,
          title: title.trim(),
          description: description || undefined,
          priority: priorityApi,
          source,
          sourceId: source === "general" ? undefined : sourceId || undefined,
          assignedTo: assignedToId,
          reviewRequired: true,
        });

        if (result.isSuccess) {
          onOpenChange(false);
          onQueryRaised?.(true);
        } else {
          console.error("Failed to create sub-query:", result.error);
          onQueryRaised?.(false);
        }
      }
      // If onSubmit prop is provided (for backward compatibility), use it
      else if (onSubmit) {
        try {
          await onSubmit({
            title: title.trim(),
            description: description,
            priority: priority,
            assignTo: String(assignTo),
            sourceDoc: sourceDoc,
          });
          onOpenChange(false);
          onQueryRaised?.(true);
        } catch (error) {
          console.error("Failed to create query:", error);
          onQueryRaised?.(false);
        }
      }
      // Otherwise, use the create query hook
      else {
        const result = await createQuery({
          title: title.trim(),
          description: description || undefined,
          priority: priorityApi,
          source,
          sourceId:
            source === QuerySource.GENERAL ? undefined : sourceId || undefined,
          assignedTo: assignedToId,
          reviewRequired: true,
        });

        if (result.isSuccess) {
          onOpenChange(false);
          onQueryRaised?.(true);
        } else {
          console.error("Failed to create query:", result.error);
          onQueryRaised?.(false);
        }
      }
    } catch (error) {
      console.error("Error submitting query:", error);
    }
  };

  return (
    <DialogLayout open={open} onOpenChange={onOpenChange}>
      <div className="flex w-144 flex-col items-start gap-6 bg-white px-8 py-8">
        <div className="flex w-full items-center justify-between">
          <span className="text-heading-2 font-heading-2 text-default-font">
            {isCreateSubQuery ? "Raise a Sub Query" : "Raise a Query"}
          </span>
          <IconButton icon={<FeatherX />} onClick={close} />
        </div>
        <div className="flex w-full flex-col items-start gap-6">
          <div className="flex w-full flex-col items-start gap-4">
            {sourceDoc ? (
              <div className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-4 py-4">
                <div className="flex w-full items-center justify-between">
                  <div className="flex items-center gap-2">
                    <IconWithBackground icon={<FeatherFileText />} />
                    <span className="text-body-bold font-body-bold text-default-font">
                      {sourceDoc.type === "credit_note" &&
                      (!sourceDoc.filename ||
                        sourceDoc.filename === "credit-note.pdf" ||
                        sourceDoc.filename
                          .toLowerCase()
                          .includes("credit-note"))
                        ? sourceDoc.id
                        : sourceDoc.filename ?? sourceDoc.id}
                    </span>
                  </div>
                  {sourceDoc.viewUrl && sourceDoc.type !== "credit_note" ? (
                    <Button
                      variant="brand-secondary"
                      size="small"
                      icon={<FeatherArrowUpRight />}
                      onClick={() => window.open(sourceDoc.viewUrl!, "_blank")}
                    >
                      View
                    </Button>
                  ) : null}
                </div>
                <div className="flex w-full items-center gap-4">
                  <Badge>
                    {sourceDoc.type === "credit_note"
                      ? "Credit Note"
                      : (sourceDoc.type || "").charAt(0).toUpperCase() +
                        (sourceDoc.type || "").slice(1)}
                  </Badge>
                  {sourceDoc.amount && sourceDoc.type !== "credit_note" ? (
                    <span className="text-body font-body text-neutral-500">
                      Amount: {sourceDoc.amount}
                    </span>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
          {/* Compliance Type Display (for compliance queries) */}
          {isComplianceQuery && complianceType && (
            <div className="flex w-full flex-col items-start gap-4">
              <span className="text-body-bold font-body-bold text-default-font">
                Compliance Information
              </span>
              <div className="flex w-full flex-col items-start gap-4">
                <TextField
                  className="h-auto w-full flex-none"
                  variant="filled"
                  label="Compliance Type"
                  helpText=""
                  disabled={true}
                >
                  <TextField.Input
                    placeholder="Compliance Type"
                    value={
                      complianceType ? formatComplianceType(complianceType) : ""
                    }
                    disabled={true}
                  />
                </TextField>
              </div>
            </div>
          )}
          <div className="flex w-full flex-col items-start gap-4">
            <span className="text-body-bold font-body-bold text-default-font">
              Assignment
            </span>
            <div className="flex w-full flex-col items-start gap-4">
              <div className="flex w-full flex-col items-start gap-2">
                <span className="text-body font-body text-subtext-color">
                  Assign To {isComplianceQuery ? "(Client)" : ""}
                </span>
                <SearchableDropdown<{
                  id: number;
                  email: string;
                  userName: string;
                  role: string;
                }>
                  fetchItems={fetchUsers}
                  initialLimit={10}
                  trigger={
                    <Button
                      variant="neutral-primary"
                      size="medium"
                      icon={<FeatherUser />}
                      className="w-full justify-start"
                    >
                      {selectedUser
                        ? `${selectedUser.userName} - ${formatRole(
                            selectedUser.role
                          )}`
                        : "Select team member..."}
                    </Button>
                  }
                  placeholder="Select team member..."
                  emptyMessage="No team members available"
                  noResultsMessage="No team members found"
                  searchPlaceholder="Search team members..."
                  getSearchableText={(user) =>
                    `${user.userName} ${user.email} ${user.role}`.trim()
                  }
                  renderItem={(user) => (
                    <div className="flex flex-col items-start w-full">
                      <span className="text-body font-body text-default-font">
                        {user.userName} - {formatRole(user.role)}
                      </span>
                      {/* <span className="text-caption font-caption text-subtext-color mt-0.5">
                        {user.email}
                      </span> */}
                    </div>
                  )}
                  getItemKey={(user) => user.id}
                  onSelect={(user) => {
                    setSelectedUser(user);
                    setAssignTo(user.id);
                    setAssignToError(false);
                  }}
                  selectedKey={assignTo || undefined}
                />
                {assignToError && (
                  <span className="text-caption font-caption text-error-600">
                    Please select a team member
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-4">
            <span className="text-body-bold font-body-bold text-default-font">
              Query Details
            </span>
            <div className="flex w-full flex-col items-start gap-4">
              <TextField
                className="h-auto w-full flex-none"
                variant="filled"
                label="Query Title"
                helpText="Enter a clear, concise title for the query"
              >
                <TextField.Input
                  placeholder="e.g. Amount mismatch with purchase order"
                  value={title}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setTitle(e.target.value)
                  }
                />
              </TextField>
              <TextArea
                className="h-auto w-full flex-none"
                variant="filled"
                label="Description"
                helpText="Provide additional details about the query"
              >
                <TextArea.Input
                  placeholder="Describe the issue in detail..."
                  value={description}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                    setDescription(e.target.value)
                  }
                />
              </TextArea>
            </div>
          </div>
          <div className="flex w-full flex-col items-start gap-4">
            <span className="text-body-bold font-body-bold text-default-font">
              Priority
            </span>
            <div className="flex w-full flex-col items-start gap-4">
              <div className="flex w-full items-center gap-6">
                <span className="text-body font-body text-subtext-color">
                  Priority Level
                </span>
                <ToggleGroup
                  value={priority}
                  onValueChange={(v: string) =>
                    setPriority(v as "Low" | "Medium" | "High")
                  }
                >
                  <ToggleGroup.Item icon={null} value="Low">
                    Low
                  </ToggleGroup.Item>
                  <ToggleGroup.Item icon={null} value="Medium">
                    Medium
                  </ToggleGroup.Item>
                  <ToggleGroup.Item icon={null} value="High">
                    High
                  </ToggleGroup.Item>
                </ToggleGroup>
              </div>
            </div>
          </div>
        </div>
        <div className="flex w-full items-center justify-end gap-2 border-t border-solid border-neutral-border pt-6">
          <Button
            variant="neutral-tertiary"
            onClick={close}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            icon={<FeatherPlus />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={!assignTo || assignTo === null || submitting}
          >
            Raise Query
          </Button>
        </div>
      </div>
    </DialogLayout>
  );
};

export default QueryRaiseModal;
